import React, { useState } from 'react';
import { View, Pressable, Switch } from 'react-native';
import { Calendar } from 'react-native-calendars';
import { DateTime } from 'luxon';
import styled from 'styled-components/native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';

import { Button } from 'app/src/elements/buttons';
import { Text } from 'app/src/styles';
import { dateRange } from './dateHelpers';

export const CalendarContainer = styled(View)`
  position: absolute;
  align-items: center;
  background-color: white;
  border-width: 2px;
  border-style: solid;
  border-color: black;
  top: 30px;
`;

export const Value = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  padding: 6px;
  gap: 20px;
`;

export const ValueLabel = styled(Text)`
  font-size: 14;
  font-weight: 900;
  color: ${props => props.disabled ? 'gray' : 'black'};
`;

const MultiDaySwitch = styled(View)`
  flex-direction: row;
  justify-content: space-around;
  gap: 20px;
  margin: 10px;
`;

const isoDate = date => date.toFormat('yyyy-MM-dd');

const CalendarPicker = props => {
  const {
    multiDay,
    setStartOn,
    setEndOn,
    setDate,
    startOn,
    endOn,
    date,
    setActive,
    availability,
    hideLabel,
  } = props;

  if (endOn && endOn < startOn) {
    throw('end on is before start on');
  }

  const [showCalendar, setShowCalendar] = useState(hideLabel);
  const [startDay, setStartDay] = useState(startOn || date || endOn);
  const [endDay, setEndDay] = useState(endOn || startOn || date);
  const [selectMultiDay, setSelectMultiDay] = useState(true === multiDay);

  const oneDay = startDay && startDay === endDay;

  const markedDates = {}
  let dates = null;

  if (startDay) {
    markedDates[startDay] = {
      startingDay: true,
      endingDay: oneDay,
      color: '#50cebb',
      textColor: 'white',
    }

    dates = dateRange(startDay, endDay);

    if (endDay !== startDay) {
      let currentDate = DateTime.fromISO(startDay);

      while (isoDate(currentDate) !== endDay) {
        currentDate = currentDate.plus({ days: 1 });

        markedDates[isoDate(currentDate)] = {
          startingDay: false,
          endingDay: isoDate(currentDate) === endDay,
          color: '#50cebb',
          textColor: 'white'
        }
      }
    }
  }

  if (availability) {
    availability.forEach(day => {
      if (!day.available) {
        markedDates[day.date] = { disabled: true, disableTouchEvent: true };
      }
    });
  }

  const updateStartDay = value => {
    setStartDay(value);
    if (setStartOn) { setStartOn(value); }
    if (setDate) { setDate(value); }
  };

  const updateEndDay = value => {
    setEndDay(value);
    if (setEndOn) { setEndOn(value); }
  };

  const allAvailable = (startDate, endDate) => {
    let checkDate = DateTime.fromISO(startDate);

    while (isoDate(checkDate) !== endDate) {
      checkDate = checkDate.plus({ days: 1 });

      if (markedDates[isoDate(checkDate)]?.disabled) {
        return false;
      }
    }

    return true;
  };

  const handleSelect = day => {
    if (day === startDay) {
      updateStartDay(null);
      updateEndDay(null);
    } else if (day === endDay) {
      updateStartDay(null);
      updateEndDay(null);
    } else if (!selectMultiDay || !startDay) {
      updateStartDay(day);
      updateEndDay(day);
    } else if (day < startDay) {
      if (allAvailable(day, startDay)) {
        updateStartDay(day);
        updateEndDay(startDay);
      }
    } else {
      if (allAvailable(startDay, day)) {
        updateEndDay(day);
      }
    }
  };

  const toggleShow = () => {
    const showValue = !showCalendar;
    setShowCalendar(showValue);
    if (setActive) { setActive(showValue); }
  };

  return (
    <View style={{zIndex: 999}}>
      {!hideLabel && (
        <Pressable onPress={toggleShow}>
          <Value>
            <ValueLabel>{dates || ' '}</ValueLabel>
            <FontAwesomeIcon icon={faCalendar} />
          </Value>
        </Pressable>
      )}
      {showCalendar && (
        <CalendarContainer>
          {'optional' === multiDay && (
            <MultiDaySwitch>
              <Text>Multiple Days?</Text>
              <Switch
                value={selectMultiDay}
                onValueChange={value => {
                  setSelectMultiDay(value);
                  if (!value) { updateEndDay(startDay) }
                }}
              />
            </MultiDaySwitch>
          )}
          <Calendar
            style={{ width: 320 }}
            theme={{ arrowWidth: 24, arrowHeight: 24 }}
            onDayPress={day => handleSelect(day.dateString)}
            markedDates={markedDates}
            markingType="period"
          />
          <Button
            label={startDay && endDay ? 'Apply' : 'Close'}
            onPress={toggleShow}
          />
        </CalendarContainer>
      )}
    </View>
  );
};

export default CalendarPicker;
